/**
 * The AQL of a Sample step of a visual-inspection operation (services/recipe/aql_setup.py), set here in one form and saved in one go:
 * the inspection level, the defect groups the AQL judges — the groups the operation's inspection specification (its defect
 * set) has, each with the defect codes in it — with the AQL of each, and the rule for how many tubs to sample from.
 *
 * Nothing is preset: a step with no setup shows empty fields and the server refuses a setup that is not whole. The sample size and the Ac / Re come
 * from the bands typed here (batch-size ranges); with none set, the AQL screen looks them up from the lot size and the AQLs in the standard's table.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Flex, InputNumber, Select, Switch, Typography } from "antd";

const { Text } = Typography;
const fs = { fontSize: "calc(12px * var(--dz-fs, 1))" };
const LEVELS = ["S-1", "S-2", "S-3", "S-4", "I", "II", "III"];
const BASES = [{ value: "ACCEPTED_UNITS", label: "Cartridges that passed visual inspection" }, { value: "THEORETICAL", label: "Theoretical batch size" }];
const SCOPES = [{ value: "LOT", label: "Each filling lot" }, { value: "BATCH", label: "The whole batch" }];
const ROUNDINGS = [{ value: "UP", label: "Round up" }, { value: "DOWN", label: "Round down" }, { value: "NEAREST", label: "Nearest" }];

const Row = ({ label, children }) => (
  <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
    <Text style={{ ...fs, width: 150, flex: "none" }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </Flex>
);

// the groups of the inspection specification: the categories its defect codes carry, with the codes of each
export function groupsOf(defectSet, names = {}) {
  const by = {};
  for (const c of defectSet?.codes || []) {
    const k = String(c.category || "").toUpperCase();
    if (k) (by[k] = by[k] || []).push(c);
  }
  return Object.keys(by).map((k) => ({ key: k, name: names[k] || k, codes: by[k] }));
}

const blankBand = () => ({ lot_min: null, lot_max: null, groups: {}, rule: { kind: undefined, value: null, plus: null, rounding: undefined } });

// a stored band shows its rule; a band that states a plain sample number is a fixed-number rule
const bandIn = (b) => ({ lot_min: b.lot_min, lot_max: b.lot_max ?? null, groups: Object.fromEntries(Object.entries(b.groups || {}).map(([g, v]) => [g.toUpperCase(), { ac: v.ac, re: v.re }])),
  rule: b.sample_rule ? { kind: b.sample_rule.kind, value: b.sample_rule.value ?? null, plus: b.sample_rule.plus ?? null, rounding: b.sample_rule.rounding }
    : { kind: "FIXED", value: b.sample ?? null, plus: null, rounding: undefined } });

export function toDraft(setup) {
  const judged = {};
  for (const g of setup?.groups || []) judged[g.group] = { aql: g.aql, aql_after: g.aql_after ?? null };
  const t = setup?.tray_rule;
  const p = setup?.plan;
  const plan = p ? { lot_basis: p.lot_basis, scope: p.scope,     normal: (p.normal || []).map(bandIn), tightened: (p.tightened || []).map(bandIn) } : { lot_basis: undefined, scope: undefined, normal: [blankBand()], tightened: [] };
  return { level: setup?.level || undefined, judged, plan,
           tray: { on: !!t, plus: t?.plus ?? 1, rounding: t?.rounding || "UP" } };
}

// a band as sent: its range and the rule its sample comes from
const bandOut = (b, judged) => {
  const r = b.rule || {};
  const rule = r.kind === "FIXED" ? { kind: "FIXED", value: r.value }
    : r.kind === "PERCENT" ? { kind: "PERCENT", value: r.value, rounding: r.rounding }
    : { kind: r.kind || "", plus: r.plus, rounding: r.rounding };
  return { lot_min: b.lot_min, lot_max: b.lot_max ?? null, sample_rule: rule,
    groups: Object.fromEntries(Object.keys(judged).map((g) => [g, { ac: b.groups?.[g]?.ac ?? null, re: b.groups?.[g]?.re ?? null }])) };
};

export function toSetup(d) {
  return { level: d.level || "",
           groups: Object.entries(d.judged).map(([group, v]) => ({ group, aql: v.aql, aql_after: v.aql_after ?? null })),
           tray_rule: d.tray.on ? { formula: "SQRT_PLUS", plus: d.tray.plus, rounding: d.tray.rounding } : null,
           plan: d.plan ? { lot_basis: d.plan.lot_basis, scope: d.plan.scope, normal: d.plan.normal.map((b) => bandOut(b, d.judged)), tightened: d.plan.tightened.map((b) => bandOut(b, d.judged)) } : null };
}

const KINDS = [{ value: "FIXED", label: "A fixed number" }, { value: "PERCENT", label: "% of the batch size" }, { value: "SQRT_PLUS", label: "√ batch size + a number" }];

function Bands({ title, hint, list, groups, onChange, readOnly, testid }) {
  const set = (i, patch) => onChange(list.map((b, k) => (k === i ? { ...b, ...patch } : b)));
  const setGroup = (i, g, patch) => set(i, { groups: { ...list[i].groups, [g]: { ...list[i].groups?.[g], ...patch } } });
  const setRule = (i, patch) => set(i, { rule: { ...list[i].rule, ...patch } });
  const line = "var(--ant-color-border-secondary, #e5e7eb)";
  return (
    <div style={{ marginTop: 14 }} data-testid={testid}>
      <Flex align="center" justify="space-between">
        <div>
          <Text strong style={{ ...fs, display: "block" }}>{title}</Text>
          {hint ? <Text type="secondary" style={fs}>{hint}</Text> : null}
        </div>
        {!readOnly ? <Button size="small" onClick={() => onChange([...list, blankBand()])} data-testid={`${testid}-add`}>+ Add band</Button> : null}
      </Flex>
      {list.length === 0 ? <Text type="secondary" style={{ ...fs, display: "block", marginTop: 6 }}>No bands.</Text> : null}
      {list.map((b, i) => (
        <div key={i} style={{ border: `1px solid ${line}`, borderRadius: 8, marginTop: 8, overflow: "hidden" }} data-testid={`${testid}-band-${i}`}>
          <Flex align="center" justify="space-between" style={{ padding: "6px 10px", background: "var(--ant-color-fill-quaternary, #f6f7f9)", borderBottom: `1px solid ${line}` }}>
            <Flex gap={6} align="center" wrap="wrap">
              <Text strong style={fs}>Band {i + 1}</Text>
              <Text type="secondary" style={fs}>batch size</Text>
              <InputNumber size="small" min={1} value={b.lot_min} disabled={readOnly} onChange={(v) => set(i, { lot_min: v })} style={{ width: 84 }} data-testid={`${testid}-min-${i}`} />
              <Text type="secondary" style={fs}>to</Text>
              <InputNumber size="small" min={1} value={b.lot_max} placeholder="no limit" disabled={readOnly} onChange={(v) => set(i, { lot_max: v })} style={{ width: 84 }} />
            </Flex>
            {!readOnly ? <Button size="small" type="text" danger onClick={() => onChange(list.filter((_, k) => k !== i))}>Remove</Button> : null}
          </Flex>
          <div style={{ padding: "8px 10px" }}>
            <Flex gap={6} align="center" wrap="wrap">
              <Text style={{ ...fs, width: 54 }}>Sample</Text>
              <Select size="small" style={{ width: 170 }} placeholder="Choose…" disabled={readOnly} value={b.rule.kind} onChange={(v) => setRule(i, { kind: v })} options={KINDS} data-testid={`${testid}-kind-${i}`} />
              {b.rule.kind === "SQRT_PLUS" ? <Text style={fs}>+</Text> : null}
              {b.rule.kind === "FIXED" || b.rule.kind === "PERCENT"
                ? <InputNumber size="small" min={1} max={b.rule.kind === "PERCENT" ? 100 : undefined} value={b.rule.value} disabled={readOnly} onChange={(v) => setRule(i, { value: v })} style={{ width: 76 }} data-testid={`${testid}-value-${i}`} /> : null}
              {b.rule.kind === "SQRT_PLUS"
                ? <InputNumber size="small" min={0} value={b.rule.plus} disabled={readOnly} onChange={(v) => setRule(i, { plus: v })} style={{ width: 76 }} data-testid={`${testid}-plus-${i}`} /> : null}
              {b.rule.kind === "PERCENT" || b.rule.kind === "SQRT_PLUS"
                ? <Select size="small" style={{ width: 110 }} placeholder="Rounding…" disabled={readOnly} value={b.rule.rounding} onChange={(v) => setRule(i, { rounding: v })} options={ROUNDINGS} data-testid={`${testid}-round-${i}`} /> : null}
            </Flex>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", columnGap: 10, rowGap: 4, alignItems: "center", marginTop: 8 }}>
              <Text type="secondary" style={fs}>Defect group</Text>
              <Text type="secondary" style={{ ...fs, width: 64, textAlign: "center" }}>Ac</Text>
              <Text type="secondary" style={{ ...fs, width: 64, textAlign: "center" }}>Re</Text>
              {groups.map((g) => (
                <React.Fragment key={g}>
                  <Text style={fs}>{g}</Text>
                  <InputNumber size="small" min={0} controls={false} value={b.groups?.[g]?.ac} disabled={readOnly} onChange={(v) => setGroup(i, g, { ac: v })} style={{ width: 64 }} data-testid={`${testid}-ac-${i}-${g}`} />
                  <InputNumber size="small" min={1} controls={false} value={b.groups?.[g]?.re} disabled={readOnly} onChange={(v) => setGroup(i, g, { re: v })} style={{ width: 64 }} data-testid={`${testid}-re-${i}-${g}`} />
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AqlSetupField({ step, readOnly = false, aql, defectSet = null }) {
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const code = step?.code;
  const load = useCallback(async () => {
    try { const s = await aql.read(code); setSaved(s); setDraft(toDraft(s)); setNote(null); }
    catch (e) { setNote({ type: "error", text: "The AQL setup could not be read." }); }
  }, [aql, code]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const fromSet = groupsOf(defectSet, saved?.group_names);
    const have = new Set(fromSet.map((g) => g.key));
    // a group already saved but no longer in the specification stays visible, so it can be taken off
    return [...fromSet, ...(saved?.groups || []).filter((g) => !have.has(g.group)).map((g) => ({ key: g.group, name: saved?.group_names?.[g.group] || g.group, codes: [], outside: true }))];
  }, [defectSet, saved]);

  if (!draft) return note ? <Alert type="error" showIcon message={note.text} /> : <Text type="secondary" style={fs}>Loading…</Text>;
  const set = (patch) => { setDraft((d) => ({ ...d, ...patch })); setNote(null); };
  const dirty = JSON.stringify(toSetup(draft)) !== JSON.stringify(toSetup(toDraft(saved)));
  const save = async () => {
    setSaving(true);
    const res = await aql.save(code, toSetup(draft));
    setSaving(false);
    if (res?.ok) { const next = res.setup || saved; setSaved(next); setDraft(toDraft(next)); setNote({ type: "success", text: "The AQL is saved." }); }
    else if (res && !res.conflict) setNote({ type: "error", text: res.message || "The AQL could not be saved." });
  };

  return (
    <div data-testid="aql-setup">
      <Row label="Inspection level">
        <Select size="small" style={{ width: 120 }} placeholder="Choose…" disabled={readOnly} value={draft.level} onChange={(v) => set({ level: v })}
                options={LEVELS.map((l) => ({ value: l, label: `Level ${l}` }))} data-testid="aql-level" />
      </Row>
      <Row label="Tubs to sample">
        <Flex gap={8} align="center" wrap="wrap">
          <Switch size="small" disabled={readOnly} checked={draft.tray.on} onChange={(on) => set({ tray: { ...draft.tray, on } })} data-testid="aql-tray-on" />
          {draft.tray.on ? (<>
            <Text style={fs}>√(tubs) +</Text>
            <InputNumber size="small" min={0} disabled={readOnly} value={draft.tray.plus} onChange={(v) => set({ tray: { ...draft.tray, plus: v } })} style={{ width: 64 }} data-testid="aql-tray-plus" />
            <Select size="small" disabled={readOnly} value={draft.tray.rounding} onChange={(v) => set({ tray: { ...draft.tray, rounding: v } })} options={ROUNDINGS} style={{ width: 110 }} />
          </>) : null}
        </Flex>
      </Row>

      <Text strong style={{ ...fs, display: "block", marginTop: 10 }}>Defect groups of the inspection specification</Text>
      {groups.length === 0 ? <Text type="secondary" style={fs}>This operation's inspection specification has no defect codes yet.</Text> : null}
      {groups.map((g) => {
        const on = !!draft.judged[g.key];
        return (
          <div key={g.key} style={{ marginTop: 6 }} data-testid={`aql-group-${g.key}`}>
            <Flex gap={8} align="center" wrap="nowrap">
              <Switch size="small" disabled={readOnly} checked={on} style={{ flex: "none" }}
                      onChange={(v) => set({ judged: v ? { ...draft.judged, [g.key]: { aql: null, aql_after: null } } : Object.fromEntries(Object.entries(draft.judged).filter(([k]) => k !== g.key)) })} />
              <Text style={{ ...fs, flex: "1 1 0", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${g.key} · ${g.name}`}>{`${g.key} · ${g.name}`}</Text>
              {on ? (<>
                <Text type="secondary" style={{ ...fs, flex: "none" }}>AQL</Text>
                <InputNumber size="small" min={0} step={0.001} disabled={readOnly} value={draft.judged[g.key].aql} onChange={(v) => set({ judged: { ...draft.judged, [g.key]: { ...draft.judged[g.key], aql: v } } })} style={{ width: 70, flex: "none" }} data-testid={`aql-aql-${g.key}`} />
                <Text type="secondary" style={{ ...fs, flex: "none" }} title="The AQL the group is judged by when the lot is inspected again after it failed">After re-inspection</Text>
                <InputNumber size="small" min={0} step={0.001} disabled={readOnly} value={draft.judged[g.key].aql_after} onChange={(v) => set({ judged: { ...draft.judged, [g.key]: { ...draft.judged[g.key], aql_after: v } } })} style={{ width: 70, flex: "none" }} data-testid={`aql-after-${g.key}`} />
              </>) : null}
            </Flex>
            {g.outside ? <Text type="warning" style={{ ...fs, display: "block", marginLeft: 44 }}>Not in this operation's inspection specification.</Text> : null}
          </div>
        );
      })}

      <Text strong style={{ ...fs, display: "block", marginTop: 14 }}>Sampling plan</Text>
      <Text type="secondary" style={{ ...fs, display: "block", marginBottom: 6 }}>As the BMR's plan table: for each batch size, the sample, and the Ac and Re of each defect group (the group's AQL is shown beside it).</Text>
      <>
        <Row label="Batch size counted as">
          <Select size="small" style={{ width: 280 }} placeholder="Choose…" disabled={readOnly} value={draft.plan.lot_basis} onChange={(v) => set({ plan: { ...draft.plan, lot_basis: v } })} options={BASES} data-testid="aql-basis" />
        </Row>
        <Row label="A sample is taken of">
          <Select size="small" style={{ width: 280 }} placeholder="Choose…" disabled={readOnly} value={draft.plan.scope} onChange={(v) => set({ plan: { ...draft.plan, scope: v } })} options={SCOPES} data-testid="aql-scope" />
        </Row>
        <Bands title="Normal inspection" hint="The first AQL sample of a lot." groups={Object.keys(draft.judged)} list={draft.plan.normal} readOnly={readOnly} onChange={(normal) => set({ plan: { ...draft.plan, normal } })} testid="aql-normal" />
        <Bands title="After re-inspection (tightened)" hint="The sample taken after a failed AQL and the re-inspection of the lot." groups={Object.keys(draft.judged)} list={draft.plan.tightened} readOnly={readOnly} onChange={(tightened) => set({ plan: { ...draft.plan, tightened } })} testid="aql-tightened" />
      </>

      {note ? <Alert type={note.type} showIcon style={{ marginTop: 10 }} message={note.text} data-testid="aql-note" /> : null}
      {!readOnly ? (
        <Flex gap={8} style={{ marginTop: 10 }}>
          <Button type="primary" size="small" loading={saving} disabled={!dirty} onClick={save} data-testid="aql-save">Save AQL</Button>
          <Button size="small" disabled={!dirty || saving} onClick={() => { setDraft(toDraft(saved)); setNote(null); }}>Discard changes</Button>
        </Flex>
      ) : null}
    </div>
  );
}
